/*****
    Author:	    CT Software Systems, LLC
    Last Modified: 260115

    241203 Added snSite.baseURL to urls and ctssJQuery.showMyApiError to api calls.

    CtrlType:       0=Text,
                    1=Fill In The Blank
                    2=Memo
                    3=CheckBox
                    4=Radio Button
    QuestionType    1=Phone
                    2=Address
                    3=Children
                    4=Service Times
                    5=Member Status
                    6=Birthday
                    7=Occupation
                    8=Age Group
    DataType        1=String,
                    2=Bool,
                    3=Int
                    4=Currency,
                    5=DateTime

*/
'use strict';

import {
    getTempKey
} from './ctssArrayUtils.js';
import {
    bindSelect,
    getDiv,
    getHiddenFld,
    getLabel,
    getMoveIcon,
    getSelect,
    getTag,
    getTextArea,
    getTextBox,
    getUrlParam
} from './ctssHtml.js';
import {
    allSettled,
    getPromise,
    getSortKeysFromTableOrder,
    initDragDropTableSort,
    savePromise
} from './ctssJQuery.js';
import { getUSStates } from './ctssUSStates.js';
import {
    confirmedPW,
    isDataModified,
    setModified,
    validEmail,
    validPW
} from './ctssVal.js';
import {
    appDebugMode,
    getAppToken,
    setMenu
} from './snSite.js';

const token = getAppToken();

/* Add PreDefined Questions Start */

const addAddressQuestion = (params) => {
    const parentConnectItem = addConnectItem({
        ctrlType: 1,
        questionType: 2,
        childCtrlType: 0,
        targetId: params.targetId,
        text: 'Address'
    });
    return addConnectItemGridRow({
        targetId: params.targetId,
        connectItem: parentConnectItem
    });
}

const addAgeGroupQuestion = (params) => {
    let parentConnectItem = addConnectItem({
        ctrlType: 0,
        questionType: 8,
        childCtrlType: 4,
        targetId: params.targetId,
        text: 'I am'
    });

    let childConnectItem = addConnectItem({
        ctrlType: 4,
        questionType: 8,
        childCtrlType: 0,
        targetId: parentConnectItem.id,
        text: 'Under 20'
    });
    childConnectItem = addConnectItem({
        ctrlType: 4,
        questionType: 8,
        childCtrlType: 0,
        targetId: childConnectItem.id,
        text: '20 to 30'
    });
    childConnectItem = addConnectItem({
        ctrlType: 4,
        questionType: 8,
        childCtrlType: 0,
        targetId: childConnectItem.id,
        text: '30 to 40'
    });
    childConnectItem = addConnectItem({
        ctrlType: 4,
        questionType: 8,
        childCtrlType: 0,
        targetId: childConnectItem.id,
        text: '40 to 50'
    });
    childConnectItem = addConnectItem({
        ctrlType: 4,
        questionType: 8,
        childCtrlType: 0,
        targetId: childConnectItem.id,
        text: '50 to 60'
    });
    childConnectItem = addConnectItem({
        ctrlType: 4,
        questionType: 8,
        childCtrlType: 0,
        targetId: childConnectItem.id,
        text: 'Above 60'
    });
    return addConnectItemGridRow({
        targetId: params.targetId,
        connectItem: parentConnectItem
    });
}

const addChildrenQuestion = (params) => {
    const parentConnectItem = addConnectItem({
        ctrlType: 1,
        questionType: 3,
        childCtrlType: 0,
        targetId: params.targetId,
        text: 'Child'
    });
    return addConnectItemGridRow({
        targetId: params.targetId,
        connectItem: parentConnectItem
    });
}

const addMemberStatusQuestion = (params) => {
    let parentConnectItem = addConnectItem({
        ctrlType: 0,
        questionType: 5,
        childCtrlType: 4,
        targetId: params.targetId,
        text: 'I am a'
    });

    let childConnectItem = addConnectItem({
        ctrlType: 4,
        questionType: 5,
        childCtrlType: 0,
        targetId: parentConnectItem.id,
        text: '1st time guest'
    });
    childConnectItem = addConnectItem({
        ctrlType: 4,
        questionType: 5,
        childCtrlType: 0,
        targetId: childConnectItem.id,
        text: '2nd time guest'
    });
    childConnectItem = addConnectItem({
        ctrlType: 4,
        questionType: 5,
        childCtrlType: 0,
        targetId: childConnectItem.id,
        text: 'Occasional attender'
    });
    childConnectItem = addConnectItem({
        ctrlType: 4,
        questionType: 5,
        childCtrlType: 0,
        targetId: childConnectItem.id,
        text: 'Regular attender'
    });
    childConnectItem = addConnectItem({
        ctrlType: 4,
        questionType: 5,
        childCtrlType: 0,
        targetId: childConnectItem.id,
        text: 'Member'
    });

    return addConnectItemGridRow({
        targetId: params.targetId,
        connectItem: parentConnectItem
    });
}

const addServiceTimesQuestion = (params) => {

    let parentConnectItem = addConnectItem({
        ctrlType: 0,
        questionType: 4,
        childCtrlType: 3,
        targetId: params.targetId,
        text: 'I prefer the following service times...'
    });

    let childConnectItem = addConnectItem({
        ctrlType: 3,
        questionType: 4,
        childCtrlType: 0,
        targetId: parentConnectItem.id,
        text: '8:00 AM'
    });
    childConnectItem = addConnectItem({
        ctrlType: 3,
        questionType: 4,
        childCtrlType: 0,
        targetId: childConnectItem.id,
        text: '9:30 AM'
    });
    childConnectItem = addConnectItem({
        ctrlType: 3,
        questionType: 4,
        childCtrlType: 0,
        targetId: childConnectItem.id,
        text: '11:00 AM'
    });

    return addConnectItemGridRow({
        targetId: params.targetId,
        connectItem: parentConnectItem
    });
}

/* Add PreDefined Questions End */

/* Add User Defined Questions Start */

const addCBOrRBChild = (params) => {

    const optionNumber = params.optionNumber ?? '';

    const childConnectItem = addConnectItem({
        ctrlType: params.childCtrlType,
        childCtrlType: 0,
        targetId: params.targetId,
        text: params.childCtrlType === 3
            ? `Checkbox option ${optionNumber}`
            : `Radio button option ${optionNumber}`
    });
    return childConnectItem.id;
}

const addCBOrRBQuestion = (params) => {

    const parentText = params.childCtrlType === 3 ? 'checkbox' : 'radio button';

    const parentConnectItem = addConnectItem({
        ctrlType: 0,
        questionType: 0,
        childCtrlType: params.childCtrlType,
        targetId: params.targetId,
        text: `Please enter your ${parentText} question here...`
    });
    let targetId = parentConnectItem.id;
    for (let i = 1; i < 6; i++) {
        targetId = addCBOrRBChild({
            childCtrlType: params.childCtrlType,
            targetId: targetId,
            optionNumber: i
        });
    }

    addConnectItemGridRow({
        targetId: params.targetId,
        connectItem: parentConnectItem
    });
}

/* Add User Defined Questions End */

const addConnectItemGridRow = (params) => {
    const targetIdx = getTargetIdx({ targetId: params.targetId });
    if (params.targetId !== undefined) {
        const $targetRow = $('#' + connectItems[targetIdx].id);
        let useAltBgColor = $targetRow.hasClass('row-alt-bg');

        $targetRow
            .after(getConnectItemRow({
                data: params.connectItem,
                /* set color to previous row - it will be changed on nextall */
                useAltBgColor: useAltBgColor
            }))
            .nextAll().each(function (index) {
                if (parseInt($(this).data('parentID'), 10) === 0) {
                    useAltBgColor = !useAltBgColor;
                }
                useAltBgColor
                    ? $(this).removeClass('row-bg').addClass('row-alt-bg')
                    : $(this).removeClass('row-alt-bg').addClass('row-bg');
            });
    } else {
        $('#gridConnectItems > tbody')
            .append(getConnectItemRow({
                data: params.connectItem,
                useAltBgColor: params.isChild === undefined
                    ? !$('#gridConnectItems tbody tr:last-of-type').hasClass('row-alt-bg') /* not child so use ! to alternate color */
                    : $('#gridConnectItems tbody tr:last-of-type').hasClass('row-alt-bg')
            }));
    }
    setConnectItemsDroppable();
    setEditableDivInputEvent({ selector: '#' + params.connectItem.id + ' div[contenteditable="true"]' });
    updateDisplayOrder({ startIdx: targetIdx });

    if (params.connectItem.childCtrlType === 3 || params.connectItem.childCtrlType === 4) {
        initChildDragDropTableSort({ id: params.connectItem.id });
    }
}

const addConnectItem = (params) => {
    // Creates connectItem and adds it to connectItems array
    let parentID;
    let targetIdx;
    if (params.targetId === undefined) {
        parentID = 0;
    } else {
        targetIdx = getTargetIdx({ targetId: params.targetId });
        parentID = params.ctrlType === 3 || params.ctrlType === 4 ||
            (params.isChildText !== undefined && params.ctrlType === 0)
            ? connectItems[targetIdx].parentID === 0
                ? connectItems[targetIdx].id
                : connectItems[targetIdx].parentID
            : 0;
    }

    const connectItem = {
        id: getTempKey({ arr: connectItems, fldNm: 'id' }),
        parentID: parentID,
        text: params.text,
        ctrlType: params.ctrlType ?? 0,
        questionType: params.questionType ?? 0,
        childCtrlType: params.childCtrlType ?? 0,
        indentLevel: 0,
        hasTextBox: false,
        isProfile: isProfile,
        isActive: true,
        childDisplayOrder: params.childDisplayOrder ?? 0,
        displayOrder: params.displayOrder ?? 0
    };

    targetIdx === undefined
        ? connectItems.push(connectItem)
        : connectItems.splice(targetIdx + 1, 0, connectItem);
    setModified(true);
    return connectItem;
}

const deleteConnectItem = (params) => {
    $('#deleteDlg').modal('hide');

    const id = parseInt(params.id, 10);
    // filter makes shallow copy so references point back to connectItems
    const itemsToDelete = connectItems.filter(row => row.id === id || row.parentID === id);
    itemsToDelete.forEach((item) => {
        if (id < 0) { // In memory splice doesn't work on shallow copy
            const idx = connectItems.findIndex(row => row.id === item.id);
            connectItems.splice(idx, 1);
        }
        else {
            item.isActive = false; // In Database so mark as inactive -
        }
    });
    setModified(true);
    getConnectItemRows();
}

const getConnectItemChildRow = (params) => {
    let content = '<tr id="' + params.id +
        '" data-parentid="' + params.parentID + '"><td class="col-md-9">';
    if (params.ctrlType === 0) {
        content += getDiv({
            content: params.text,
            attrs: {
                id: 'edit' + params.id,
                contenteditable: true,
                cssClass: getIndentLevelClass(params.indentLevel)
            }
        });
    } else {
        content += '<div id="div' + params.id + '">' +
            getCBOrRB({
                id: params.id,
                text: params.text,
                ctrlType: params.ctrlType,
                indentLevel: params.indentLevel,
                hasTextBox: params.hasTextBox
            }) + '</div>';
    }
    content += '</td><td class="col-md-1"> </td><td class="col-md-2 text-right">';
    if (params.ctrlType === 3 || params.ctrlType === 4) {
        content += '<span title="Click to add or remove blank line..." ' +
            'class="fa fa-window-minimize mRight5" ' +
            'onclick= "snConnect.toggleBlankLine({ id: ' + params.id + ' })"></span > ';
    }
    content +=
        '<span title="Indent item..." class="fa fa-indent mRight5" ' +
        'onclick= "snConnect.indentItem({ id: ' + params.id + ', ctrlType: ' + params.ctrlType + ' })"></span > ' +
        '<span title="Dedent item..." class="fa fa-dedent mRight5" ' +
        'onclick= "snConnect.dedentItem({ id: ' + params.id + ', ctrlType: ' + params.ctrlType + ' })"></span > ' +
        '<span class="fa fa-trash mRight5" data-toggle="modal" data-target="#deleteDlg" data-key="' + params.id + '"></span>' +
        getMoveIcon({ handle: 'moveChildHandle' });
    content += '</td></tr>';
    return content;
}

const getConnectItemRow = (params) => {
    const qType = params.data.questionType;
    if ((qType === 1 || qType === 2 || qType === 3)
        && (compoundQuestionRowDrawn.indexOf(qType) > -1)) {
        return;
    } else {
        compoundQuestionRowDrawn.push(qType);
    }
    let text = '';
    switch (qType) {
        case 1: text = getPhoneQuestion(); break;
        case 2: text = getAddressQuestionFormat(); break;
        case 3: text = getChildrenQuestionFormat(); break;
        case 6: text = getBirthdayQuestion(); break;
        case 7: text = getOccupationQuestion(); break;
        default:
            switch (params.data.ctrlType) {
                case 0: text = getEditableText({
                    id: params.data.id,
                    text: params.data.text
                }); break;
                case 1: text = getFillIntheBlank({
                    id: params.data.id,
                    text: params.data.text
                }); break;
                case 2: text = getMemo({
                    id: params.data.id,
                    text: params.data.text
                }); break;
                case 3: text = getCBOrRB({
                    id: params.data.id,
                    text: params.data.text,
                    ctrlType: 3,
                    indentLevel: params.data.indentLevel,
                    hasTextBox: params.data.hasTextBox
                }); break;
                case 4: text = getCBOrRB({
                    id: params.data.id,
                    text: params.data.text,
                    ctrlType: 4,
                    indentLevel: params.data.indentLevel,
                    hasTextBox: params.data.hasTextBox
                }); break;
            }
    }
    const rowColorClass = params.useAltBgColor === true ? ' class="row-alt-bg" ' : ' class="row-bg" ';
    let row = '<tr id="' + params.data.id + '"' + rowColorClass +
        ' data-displayOrder="' + params.data.displayOrder +
        '" data-parentid="' + params.data.parentID + '"><td><div class="col-md-10">' +
        text + '</div><div class="col-md-2 text-right">';
    switch (params.data.childCtrlType) {
        case 3:
            row += '<span title="Add checkbox..." ' +
                'class="fa fa-check-square mRight5" ' +
                'onclick= "snConnect.addCBOrRB({ parentId: ' + params.data.id + ', childCtrlType: 3 })"></span > ';
            row += '<span title="Add text..." class="mRight5" style="font-weight: bold; font-style: italic"' +
                'onclick= "snConnect.addChildText({ parentId: ' + params.data.id + ', childCtrlType: 3 })">T</span > ';
            break;
        case 4:
            row += '<span title="Add radio button..." ' +
                'class="fa fa-dot-circle-o mRight5" ' +
                'onclick= "snConnect.addCBOrRB({ parentId: ' + params.data.id + ', childCtrlType: 4 })"></span > ';
            row += '<span title="Add text..." class="mRight5" style="font-weight: bold; font-style: italic"' +
                'onclick= "snConnect.addChildText({ parentId: ' + params.data.id + ', childCtrlType: 4 })">T</span > ';
            break;
    }
    row += '<span class="fa fa-trash mRight5" data-toggle="modal" data-target="#deleteDlg" data-key="' + params.data.id + '"></span>' +
        getMoveIcon() + '</div>';
    if (params.data.childCtrlType === 3 || params.data.childCtrlType === 4) {
        if (appDebugMode) {
            console.log('connectitems', connectItems);
        }
        const children = connectItems.filter(row => parseInt(row.parentID, 10) === parseInt(params.data.id, 10));
        if (appDebugMode) {
            console.log('children', children);
        }
        row += '<table id="grid' + params.data.id + '" class="col-md-12"><tbody>';
        for (let i = 0, l = children.length; i < l; i++) {
            const child = children[i];
            if (child.isActive === true) {
                row += getConnectItemChildRow({
                    id: child.id,
                    parentID: params.data.id,
                    text: child.text,
                    ctrlType: child.ctrlType,
                    indentLevel: child.indentLevel,
                    hasTextBox: child.hasTextBox
                });
            }
        }
        row += '</tbody></table>';
    }
    row += '</td></tr>';
    return row;
}

const getConnectItemRows = () => {
    compoundQuestionRowDrawn.length = 0;
    let content = '';
    let useAltBgColor = true;
    for (let i = 0, l = connectItems.length; i < l; i++) {
        if (parseInt(connectItems[i].parentID) === 0 && connectItems[i].isActive === true) {
            useAltBgColor = !useAltBgColor;
            content += getConnectItemRow({ data: connectItems[i], useAltBgColor: useAltBgColor });
        }
    }
    $('#gridConnectItems > tbody').empty().append(content);

    setConnectItemsDroppable();
    setEditableDivInputEvent({ selector: 'div[contenteditable="true"]' });
    for (let i = 0, l = connectItems.length; i < l; i++) {
        if (connectItems[i].childCtrlType === 3 || connectItems[i].childCtrlType === 4) {
            initChildDragDropTableSort({ id: connectItems[i].id });
        }
    }
}
const getIndentLevelClass = (indentLevel) => {
    return indentLevel === 0
        ? ''
        : indentLevel > 2
            ? 'indent2'
            : 'indent' + indentLevel;
}

const getPredefinedQuestionsAsync = async () =>
    await getPromise({ url: 'CCards/PredefinedQuestions' });

const getTargetIdx = (params = {}) =>
    params.targetId === undefined
        ? 0
        : connectItems.findIndex(r => parseInt(r.id, 10) === parseInt(params.targetId, 10));

///* predefined question formats begin */

const getAddressQuestionFormat = () => {
    return getDiv({
        content: getLabel({ content: 'Address', attrs: { cssClass: 'w75' } }) +
            getTextBox({ ctrlAttrs: { cssClass: 'w300 mLeft10', readonly: true } })
    }) + getDiv({
        content: getTextBox({ ctrlAttrs: { cssClass: 'w300 mLeft85', readonly: true } })
    }) + getDiv({
        content: getTextBox({ ctrlAttrs: { cssClass: 'w150 mLeft85 mLeft5', readonly: true } }) +
            getLabel({ content: ',', attrs: { cssClass: 'mLeft5 mRight5' } }) +
            getTextBox({ ctrlAttrs: { cssClass: 'w25 mRight10', readonly: true } }) +
            getTextBox({ ctrlAttrs: { cssClass: 'w100', readonly: true } })
    });
}

const getChildrenQuestionFormat = () => {
    return getTextBox({
        labelContent: 'Child First Name',
        labelAttrs: { cssClass: 'w115' },
        ctrlAttrs: {
            cssClass: 'w150 mLeft10 mRight10',
            readonly: true
        }
    }) + getTextBox({
        labelContent: 'Last',
        ctrlAttrs: {
            cssClass: 'w150 mLeft10 mRight10',
            readonly: true
        }
    }) + getDiv({
        content: getTextBox({
            labelContent: 'Birthday',
            labelAttrs: { cssClass: 'w115' },
            ctrlAttrs: { cssClass: 'w50 mLeft10', readonly: true }
        })
    });
}

const getPhoneQuestion = () => {
    return getTextBox({
        labelContent: 'Phone',
        labelAttrs: { cssClass: 'w75' },
        ctrlAttrs: {
            cssClass: 'w200 mLeft10 mRight10',
            readonly: true
        }
    });
}

const getBirthdayQuestion = () => {
    return getTextBox({
        labelContent: 'Birthday',
        labelAttrs: { cssClass: 'w75' },
        ctrlAttrs: {
            cssClass: 'w100 mLeft10 mRight10',
            readonly: true
        }
    });
}

const getOccupationQuestion = () => {
    return getTextBox({
        labelContent: 'Occupation',
        labelAttrs: { cssClass: 'w75' },
        ctrlAttrs: {
            cssClass: 'w200 mLeft10 mRight10',
            readonly: true
        }
    });
}

/* predefined question formats end */

/* generic question formats start */

const getCBOrRB = (params) => {
    let content = getTag({
        tag: 'input',
        attrs: {
            id: params.ctrlType === 3 ? 'cb' + params.id : 'rb' + params.id,
            type: params.ctrlType === 3 ? 'checkbox' : 'radio',
            readonly: true,
            cssClass: getIndentLevelClass(params.indentLevel)
        }
    }) + getDiv({
        content: params.text,
        attrs: { id: 'edit' + params.id, contenteditable: true, cssClass: 'ib' }
    });
    if (params.hasTextBox === true) {
        content += getTextBox({ ctrlAttrs: { cssClass: 'added-textbox', readonly: true } });
    }
    return content;
}

const getEditableText = (params) => {
    return getDiv({
        content: params.text,
        attrs: {
            id: 'edit' + params.id, contenteditable: true, cssClass: 'fwBold'
        }

    });
}

const getFillIntheBlank = (params) => {
    return getDiv({
        content: params.text,
        attrs: { id: 'edit' + params.id, contenteditable: true }
    }) + getDiv({
        content: getTextBox({ ctrlAttrs: { cssClass: 'w300', readonly: true } })
    });
}

const getMemo = (params) => {
    return getDiv({
        content: params.text,
        attrs: { id: 'edit' + params.id, contenteditable: true }
    }) + getDiv({
        content: getTextArea({ ctrlAttrs: { cssClass: 'w350', rows: 3, readonly: true } })
    });
}

const initChildDragDropTableSort = (params) => {

    const selector = `#grid${params.id} > tbody`;
    initDragDropTableSort({
        selector: selector,
        connectswith: selector,
        handle: '.moveChildHandle',
        fnStop: () => {
            const parentId =
                parseInt($(`#grid${params.id} > tbody > tr:first-child`).attr('data-parentid'), 10);
            let idx = connectItems.findIndex(row => parseInt(row.id, 10) === parentId);
            const sortedKeys = getSortKeysFromTableOrder({
                selector: `#grid${params.id} > tbody > tr`,
                hasChildren: true,
                parentDisplayOrder: connectItems[idx].displayOrder
            });
            for (let i = 0, l = sortedKeys.length; i < l; i++) {
                let idx = connectItems.findIndex(row => parseInt(row.id, 10) === parseInt(sortedKeys[i].id, 10));
                if (idx > -1) {
                    connectItems[idx].displayOrder = sortedKeys[i].displayOrder;
                    connectItems[idx].childDisplayOrder = sortedKeys[i].childDisplayOrder;
                }
            }
            setModified(true);
        }
    });
}

const initDeleteDlg = () => {
    $('#deleteDlg').on('show.bs.modal', (e) => {
        const key = e.relatedTarget.dataset.key;
        const item = connectItems.find(x => x.id === parseInt(key, 10));
        let itemDesc = '';
        if (item.questionType === 0) {
            switch (item.childCtrlType) {
                case 3: itemDesc = item.text + ' checkbox question'; break;
                case 4: itemDesc = item.text + ' radio button question'; break;
                default: itemDesc = item.text + ' question'; break;
            }
        } else {
            switch (item.questionType) {
                case 1: itemDesc = 'phone question'; break;
                case 2: itemDesc = 'address question'; break;
                case 3: itemDesc = 'children question'; break;
                case 4: itemDesc = 'service times question'; break;
                case 5: itemDesc = 'member status question'; break;
                case 6: itemDesc = 'birthday question'; break;
                case 7: itemDesc = 'occupation question'; break;
                case 8: itemDesc = 'age group question'; break;
                default:
                    itemDesc = item.text + ' question'
                    break;
            }
        }
        $('#deletePrompt').text('Are you sure you want to delete the ' + itemDesc + '?');
        $('#btnDelete').off('click').click(() => { deleteConnectItem({ id: key }); });
    });
}

const initQuestionsDlg = () => {
    $('#questionsDlg').on('show.bs.modal', (e) => {
        let source = []; /* get all questions not already in use */
        for (let i = 0, l = predefinedQuestions.length; i < l; i++) {
            if (connectItems.findIndex(item => item.questionType === predefinedQuestions[i].value) === -1) {
                source.push(predefinedQuestions[i]);
            }
        }
        $('#divQuestionsDlgBody')
            .append(getSelect({
                items: source,
                ctrlAttrs: { id: 'questionsDDL', cssClass: 'form-control' }
            }));
    });
}

const saveConnectItemsAsync = async (params) =>
    await savePromise({
        url: 'CCards/CCardItems',
        data: {
            cg: token.CG,
            cCardItems: params.connectItems
        }
    });

const setConnectItemsDroppable = () => {
    $('#gridConnectItems tbody tr').droppable({
        accept: '.draggable-ctrl',
        greedy: true, // prevents event from bubbling to parent when control is dropped on child table
        drop: function (event, ui) {
            let $target = $('#' + this.id);
            let parentId = $target.attr('data-parentid');
            if (parentId !== "0") {
                // change target from child record to parent record so new
                // question is added after child rows for this question.
                $target = $('#' + parentId);
            }
            switch (ui.draggable[0].id) {
                case 'btnAddText':
                    addText({ targetId: $target[0].id });
                    break;
                case 'btnFillInQuestion':
                    addFillInTheBlank({ targetId: $target[0].id });
                    break;
                case 'btnCBQuestion':
                    addCheckBoxes({ targetId: $target[0].id });
                    break;
                case 'btnPredefinedQuestion':
                    addPredefinedQuestion({ targetId: $target[0].id });
                    break;
                case 'btnMemoQuestion':
                    addMemoQuestion({ targetId: $target[0].id });
                    break;
                case 'btnRBQuestion':
                    addRadioButtons({ targetId: $target[0].id });
                    break;
            }
        }
    });
}

const setEditableDivInputEvent = (params) => {
    $(params.selector).on('input', function () {
        setModified(true);
        const idx = connectItems.findIndex(row => parseInt(row.id, 10) === parseInt(this.id.substring(4), 10));
        connectItems[idx].text = $('#' + this.id).html().replace(/&nbsp;/g, '');
    });
}

const selectPredefinedQuestion = () => {
    let targetId;
    const id = $('#targetId').val();
    if (id.length > 0) {
        targetId = id;
    };
    const questionType = parseInt($('#questionsDDL').val(), 10);
    switch (questionType) {
        case 1: addFillInTheBlank({
            targetId: targetId,
            questionType: 1,
            text: 'Phone',
            dataType: 1
        }); break;
        case 2: addAddressQuestion({}); break;
        case 3: addChildrenQuestion({}); break;
        case 4: addServiceTimesQuestion({ targetId: targetId }); break;
        case 5: addMemberStatusQuestion({ targetId: targetId }); break;
        case 6: addFillInTheBlank({
            targetId: targetId,
            questionType: 6,
            text: 'Birthday',
            dataType: 5
        }); break;
        case 7: addFillInTheBlank({
            targetId: targetId,
            questionType: 7,
            text: 'Occupation',
            dataType: 1
        }); break;
        case 8: addAgeGroupQuestion({ targetId: targetId }); break;
        default:
            addConnectItemGridRow({
                targetId: targetId,
                connectItem: addConnectItem({
                    ctrlType: 0,
                    questionType: questionType,
                    targetId: targetId,
                    text: $('#questionsDDL option:selected').text()
                })
            });
    }
    $('#questionsDlg').modal('hide');
}

const updateDisplayOrder = (params) => {
    const startIdx = (params.startIdx === undefined) ? 0 : params.startIdx;
    let order = connectItems[startIdx].displayOrder;
    let childOrder = connectItems[startIdx].childDisplayOrder;
    for (let i = startIdx + 1, l = connectItems.length; i < l; i++) {
        let ci = connectItems[i];
        if (ci.ctrlType === 3 || ci.ctrlType === 4) { // This is a child row
            ci.displayOrder = order;
            ci.childDisplayOrder = ++childOrder;
        } else {
            ci.displayOrder = ++order;
            ci.childDisplayOrder = 0;
            childOrder = 0;
        }
    }
}

///* generic question formats ends */

let compoundQuestionRowDrawn = [];
let connectItems = [];
let isProfile;
let predefinedQuestions = [];

const addCBOrRB = (params) => {

    const parentId = parseInt(params.parentId, 10);

    const optionNumber = /* gets next option number */
        connectItems.filter(item => parseInt(item['parentID'], 10) === parentId).length + 1

    const $target = (optionNumber > 1)
        ? $(`#${parentId} > td tr:last-child`)
        : $(`#${parentId}`);

    const childId = addCBOrRBChild({
        childCtrlType: params.childCtrlType,
        targetId: $target.attr('id'),
        optionNumber: optionNumber
    });

    const child = connectItems.find(row => parseInt(row.id, 10) === parseInt(childId, 10));
    $target.after(getConnectItemChildRow({
        id: child.id,
        text: child.text,
        ctrlType: child.ctrlType,
        indentLevel: child.indentLevel
    }));
    setEditableDivInputEvent({ selector: `#${child.id} div[contenteditable="true"]` });
    updateDisplayOrder({
        startIdx: connectItems.findIndex(r => parseInt(r.id, 10) === parseInt($target.attr('id'), 10))
    });
};

const addCheckBoxes = (params) => {
    params.childCtrlType = 3;
    addCBOrRBQuestion(params);
};

const addChildText = (params) => {
    const parentId = parseInt(params.parentId, 10);

    const hasChild = connectItems.some(row => parseInt(row.parentID, 10) === parentId);
    const $target = hasChild
        ? $(`#${parentId} > td tr:last-child`)
        : $(`#${parentId}`);

    const child = addConnectItem({
        ctrlType: 0,
        childCtrlType: 0,
        targetId: $target.attr('id'),
        isChildText: true,
        text: 'Enter your text...'
    });

    $target.after(getConnectItemChildRow({
        id: child.id,
        text: child.text,
        ctrlType: child.ctrlType,
        indentLevel: child.indentLevel
    }));
    setEditableDivInputEvent({ selector: `#${child.id} div[contenteditable="true"]` });
    updateDisplayOrder({ startIdx: connectItems.findIndex(r => parseInt(r.id, 10) === parseInt($target.attr('id'), 10)) });
};

const addFillInTheBlank = (params) => {
    addConnectItemGridRow({
        targetId: params.targetId,
        connectItem: addConnectItem({
            ctrlType: 1,
            questionType: params.questionType ?? 0,
            childCtrlType: 0,
            targetId: params.targetId,
            text: params.text ?? 'Please enter your question here...',
            dataType: params.dataType ?? 1
        })
    });
}

const addMemoQuestion = (params) => {
    addConnectItemGridRow({
        targetId: params.targetId,
        connectItem: addConnectItem({
            ctrlType: 2,
            targetId: params.targetId,
            text: 'Please enter your memo question here...'
        })
    });
}

const addPredefinedQuestion = (params) => {
    $('#divQuestionsDlgBody')
        .empty()
        .append(getHiddenFld({ id: 'targetId', value: params.targetId }));
    $('#questionsDlg').modal('show');
};

const addRadioButtons = (params) => {
    params.childCtrlType = 4;
    addCBOrRBQuestion(params);
};

const addText = (params) => {
    addConnectItemGridRow({
        targetId: params.targetId,
        connectItem: addConnectItem({
            ctrlType: 0,
            targetId: params.targetId,
            text: 'Please enter your text here...'
        })
    });
};

const dedentItem = (params) => {
    const targetIdx = connectItems.findIndex(r => parseInt(r.id, 10) === parseInt(params.id, 10));
    let indentLevel = connectItems[targetIdx].indentLevel;
    indentLevel === 0 ? 0 : indentLevel--;
    connectItems[targetIdx].IndentLevel = indentLevel;

    const cssClass = getIndentLevelClass(indentLevel);
    const ctrlPrefixNm = params.ctrlType === 0
        ? 'edit'
        : params.ctrlType === 3 ? 'cb' : 'rb';
    cssClass === undefined
        ? $('#' + ctrlPrefixNm + params.id)
            .removeClass('indent1')
            .removeClass('indent2')
        : $('#' + ctrlPrefixNm + params.id)
            .removeClass('indent1')
            .removeClass('indent2')
            .addClass(cssClass);
};

const indentItem = (params) => {
    const targetIdx = connectItems.findIndex(r => parseInt(r.id, 10) === parseInt(params.id, 10));
    let indentLevel = connectItems[targetIdx].indentLevel;
    indentLevel === 2 ? 2 : indentLevel++;
    connectItems[targetIdx].indentLevel = indentLevel;

    const cssClass = getIndentLevelClass(indentLevel);
    const ctrlPrefixNm = params.ctrlType === 0
        ? 'edit'
        : params.ctrlType === 3 ? 'cb' : 'rb';
    cssClass === undefined
        ? $('#' + ctrlPrefixNm + params.id)
            .removeClass('indent1')
            .removeClass('indent2')
        : $('#' + ctrlPrefixNm + params.id)
            .removeClass('indent1')
            .removeClass('indent2')
            .addClass(cssClass);
};

const getConnectItemsDataAsync = async () =>
    await getPromise({ url: `CCards/${token.CG}/CCardItems/${isProfile}` });

const initAsync = async (params = {}) => {
    window.onbeforeunload = () => {
        if (isDataModified() === true) {
            return "Your changes have not been saved...";
        }
    };
    setMenu();
    isProfile = params.isProfile ?? false;

    const [predefinedQuestionsData, connectItemsData] = await allSettled({
        promises: [
            getPredefinedQuestionsAsync(),
            getConnectItemsDataAsync()
        ]
    });

    if (predefinedQuestionsData.status !== 'rejected' &&
        connectItemsData.status !== 'rejected') {
        predefinedQuestions = predefinedQuestionsData.value;
        connectItems = connectItemsData.value;
        getConnectItemRows();
    }

    initDeleteDlg();
    initQuestionsDlg();

    $('.draggable-ctrl').draggable({ cancel: false, helper: 'clone' });

    initDragDropTableSort({
        selector: '#gridConnectItems > tbody',
        fnStop: () => {
            const sortedKeys = getSortKeysFromTableOrder({
                selector: '#gridConnectItems > tbody > tr'
            });
            for (let i = 0, l = sortedKeys.length; i < l; i++) {
                const idx = connectItems.findIndex(row => parseInt(row.id, 10) === parseInt(sortedKeys[i].id, 10));
                if (idx > -1) {
                    connectItems[idx].displayOrder = sortedKeys[i].displayOrder;
                }
            }
            // Must physically sort array so grid can be redrawn with alternating color.
            connectItems.sort((a, b) =>
                a.displayOrder - b.displayOrder || a.childDisplayOrder - b.childDisplayOrder
            );
            setModified(true);
            getConnectItemRows();
        }
    });
    $('#btnSelectQuestion').on('click', selectPredefinedQuestion);
    $('#btnAddText').on('click', addText);
    $('#btnFillInQuestion').on('click', addFillInTheBlank);
    $('#btnCBQuestion').on('click', addCheckBoxes);
    $('#btnPredefinedQuestion').on('click', addPredefinedQuestion);
    $('#btnMemoQuestion').on('click', addMemoQuestion);
    $('#btnRBQuestion').on('click', addRadioButtons);
    $('#btnSave').on('click', saveChangesAsync);
}


const toggleBlankLine = (params) => {
    let connectItem = connectItems.find(r => parseInt(r.id, 10) === parseInt(params.id, 10));
    connectItem.hasTextBox = !connectItem.hasTextBox;
    $('#div' + params.id)
        .empty()
        .append(getCBOrRB({
            id: params.id,
            text: connectItem.text,
            ctrlType: connectItem.ctrlType,
            indentLevel: connectItem.indentLevel,
            hasTextBox: connectItem.hasTextBox
        }));
};


const saveChangesAsync = async () => {

    document.getElementById("loader").style.display = "block";

    await saveConnectItemsAsync({ connectItems: connectItems })
        .then(async () => {
            try {
                connectItems = await getConnectItemsDataAsync()
                await savePromise({
                    url: 'CCards/CCardItemsDoc',
                    data: {
                        cg: token.CG,
                        cCardItems: connectItems
                    }
                });

                getConnectItemRows()
            }
            catch (error) { }
        });

    document.getElementById("loader").style.display = "none";

};

window.snConnect = {
    addCBOrRB,
    addChildText,
    dedentItem,
    getConnectItemsDataAsync,
    indentItem,
    initAsync,
    saveChangesAsync,
    toggleBlankLine
};